import PrescriptionEligibilityModal from './PrescriptionEligibilityModal';
import useRecetas from '../hooks/usePuedeEmitirRecetas';
import PatientRecords from './PatientRecords';
import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useProfessionalData } from '../hooks/useProfessionalData';
import DashboardProfileHeader from './DashboardProfileHeader';
import SubscriptionBanner from './SubscriptionBanner';
import VencimientoSuscripcion from './VencimientoSuscripcion';
import SubscriptionExpirySoonBanner from './SubscriptionExpirySoonBanner';
import WhatsAppQuotaIndicator from './WhatsAppQuotaIndicator';
import LoadingSpinner from './LoadingSpinner';
import SubscriptionModal from './SubscriptionModal';
import SubscriptionExpiredModal from './SubscriptionExpiredModal';
import { isSubscriptionExpiredError } from '../utils/subscriptionUtils';
import { getExtraLabel, formatExtraValue } from '../utils/patientExtraData';

import ShareProfileButton from './ShareProfileButton';
import RemindersQuickAccess from './RemindersQuickAccess';
import { useSubscription } from '../hooks/useSubscription';
import OnboardingChecker from './OnboardingChecker';

// Importar iconos de Lucide
import {
  Calendar,
  Users,
  Image,
  BarChart3,
  Settings,
  Clock,
  DollarSign,
  MessageCircle,
  CreditCard,
  Plus,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileText,
  FileSpreadsheet,
  ClipboardList,  // ✅ NUEVO: Icono para Historias Clínicas
  FileSignature,  // ✅ NUEVO: Icono para Recetas Electrónicas
  MapPin,           // ✅ NUEVO: Icono para Sedes
  ListChecks,       // ✅ NUEVO: Icono para Campos de Booking
  HelpCircle
} from 'lucide-react';
import ExportDateRangeModal from './ExportDateRangeModal';
import RemindersPanel from './RemindersPanel';
import NewRemindersGuideModal from './NewRemindersGuideModal';
import AppointmentNoteEditor from './AppointmentNoteEditor';


// =====================================================
// INTERFACES LOCALES
// =====================================================

interface HealthUser {
  id: number;
  email: string;
  nombre: string;
  apellido: string;
  es_profesional: boolean;
  professional_type: 'salud';
  especialidad: string;
  auth_method: string;
  picture?: string;
  cover_photo?: string;
}

// =====================================================
// FUNCIONES DE UTILIDAD
// =====================================================

const getToday = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatLocalDate = (dateString: string) => {
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('es-ES', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

const HealthProfessionalDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const [user, setUser] = useState<HealthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(getToday());
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const [loadingClients, setLoadingClients] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showRemindersPanel, setShowRemindersPanel] = useState(false);
  const [showRemindersGuide, setShowRemindersGuide] = useState(false);
  const [showPrescriptionPendingModal, setShowPrescriptionPendingModal] = useState(false);
  const [showEligibilityModal, setShowEligibilityModal] = useState(false);
  // "Nuevo" en la tarjeta de Recetas: se apaga la primera vez que entra.
  const [badgeRecetasVisto, setBadgeRecetasVisto] = useState(
    () => localStorage.getItem('consultar:badge_recetas_visto') === '1'
  );
  // Gobierna el botón "Crear receta" del detalle de turno.
  const { mostrar: mostrarBotonReceta, puedeEmitir: puedeEmitirRecetas } = useRecetas();

  // La guía de recordatorios ya NO se abre sola (a usuarios con localStorage
  // no persistente les aparecía en cada ingreso). Queda accesible solo desde
  // los botones "ver guía" del dashboard.

  const { isExpired, daysRemaining } = useSubscription();

  const {
    metrics,
    professional,
    appointments,
    services,
    loadingMetrics,
    loadingAppointments,
    loadingServices,
    metricsError,
    appointmentsError,
    servicesError,
    actionLoading,
    actionError,
    updateAppointmentStatus,
    refreshData,
    clearErrors,
    clearActionState,
    getAvailableActions,
    isDateInPast,
    isDateToday
 } = useProfessionalData({
  professionalId: user?.id,
  autoLoad: false,
  selectedDate,
  enabled: currentPath !== '/professional/patients'
});

  // =====================================================
  // EFECTOS
  // =====================================================

  useEffect(() => {
    const initDashboard = async () => {
      const userData = localStorage.getItem('user');
      const loginStatus = localStorage.getItem('isLoggedIn');
      
      if (!userData || loginStatus !== 'true') {
        navigate('/login');
        return;
      }

      try {
        const parsedUser = JSON.parse(userData);
        
        if (!parsedUser.es_profesional || parsedUser.professional_type !== 'salud') {
          navigate('/dashboard/profesional');
          return;
        }
        
        // CONSULTAR EL BACKEND PRIMERO para obtener status actualizado
        try {
          const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
          const token = localStorage.getItem('token');
          
          if (token) {
            const response = await fetch(`${API_URL}/api/auth/me`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            
            if (response.ok) {
              const data = await response.json();
              const freshUser = data.user;
              
              // Actualizar localStorage con datos frescos
              localStorage.setItem('user', JSON.stringify(freshUser));
              localStorage.setItem('subscription_status', freshUser.subscription_status);
              
              console.log('✅ Usuario actualizado desde backend:', {
                subscription_status: freshUser.subscription_status
              });
              
              // Ahora verificar con datos frescos: tanto trial como pago vencidos
              // Preferimos freshUser.subscription.isExpired (calculado en backend)
              // Fallback: chequeo del status persistido
              const expiredByState = freshUser.subscription && freshUser.subscription.isExpired;
              const expiredByStatus =
                freshUser.subscription_status === 'trial_expired' ||
                freshUser.subscription_status === 'expired';
              if (expiredByState || expiredByStatus) {
                console.log('🚫 Suscripción expirada detectada:', {
                  effectiveStatus: freshUser.subscription && freshUser.subscription.effectiveStatus,
                  subscription_status: freshUser.subscription_status,
                });
                setUser(freshUser);
                setShowExpiredModal(true);
                setLoading(false);
                return;
              }
              
              setUser(freshUser);
              console.log('🩺 Usuario de salud verificado:', freshUser);
              setLoading(false);
              return;
            }
          }
        } catch (error) {
          console.error('Error consultando backend:', error);
          // Continuar con localStorage si falla
        }
        
        // Si falló la consulta, usar localStorage (fallback)
        const subscriptionStatus = localStorage.getItem('subscription_status');
        const trialEndsAt = localStorage.getItem('trial_ends_at');
        
        // Detectar si el backend ya marcó como expirado (trial o pago)
        if (subscriptionStatus === 'trial_expired' || subscriptionStatus === 'expired') {
          console.log('🚫 Suscripción expirada detectada por backend:', subscriptionStatus);
          setUser(parsedUser);
          setShowExpiredModal(true);
          setLoading(false);
          return;
        }
        
        // O verificar manualmente si está en trial y la fecha pasó
        if (subscriptionStatus === 'trial' && trialEndsAt) {
          const trialEndDate = new Date(trialEndsAt);
          const now = new Date();
          
          if (now > trialEndDate) {
            console.log('🚫 Trial expirado detectado por fecha');
            setUser(parsedUser);
            setShowExpiredModal(true);
            setLoading(false);
            return;
          }
        }
        
        setUser(parsedUser);
        console.log('🩺 Usuario de salud verificado:', parsedUser);
        
      } catch (error) {
        console.error('Error parsing user data:', error);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    
    initDashboard();
  }, [navigate]);

  useEffect(() => {
    const refreshUserData = async () => {
      // NO REFRESCAR SI EL MODAL DE EXPIRACIÓN ESTÁ ACTIVO
      if (showExpiredModal) {
        console.log('⏸️ Refresh de usuario pausado - Trial expirado');
        return;
      }
      
      if (!user?.id) return;
      
      try {
        console.log('🔄 Refrescando datos de usuario desde la BD...');
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const response = await fetch(`${API_URL}/api/professionals/${user.id}`);
        const data = await response.json();
        
        if (data.success && data.professional) {
          const updatedUser = { 
            ...user, 
            picture: data.professional.picture,
            cover_photo: data.professional.cover_photo 
          };
          localStorage.setItem('user', JSON.stringify(updatedUser));
          setUser(updatedUser);
          console.log('✅ Datos de usuario actualizados:', {
            picture: !!updatedUser.picture,
            cover_photo: !!updatedUser.cover_photo
          });
        }
      } catch (error: any) {
        console.error('Error refrescando datos de usuario:', error);
        
        // Detectar error 403 - Suscripción vencida
        if (isSubscriptionExpiredError(error)) {
          setShowExpiredModal(true);
          return;
        }
      }
    };
    
    if (user?.id) {
      refreshUserData();
    }
  }, [user?.id, showExpiredModal]);

  useEffect(() => {
    // NO CARGAR DATOS SI EL MODAL DE EXPIRACIÓN ESTÁ ACTIVO
    if (showExpiredModal) {
      console.log('⏸️ Carga de datos pausada - Trial expirado');
      return;
    }
    
    if (user?.id && !loading) {
      console.log('🔄 Cargando datos médicos para profesional:', user.id);
      refreshData();
    }
  }, [user?.id, loading, showExpiredModal]);

  useEffect(() => {
    if (isExpired && !showSubscriptionModal) {
      setShowSubscriptionModal(true);
    }
  }, [isExpired, showSubscriptionModal]);

  // =====================================================
  // FUNCIONES DE MANEJO
  // =====================================================

  const handleAppointmentAction = async (appointmentId: number, action: string) => {
    if (!user?.id) return;
    
    console.log(`🎯 Acción médica ${action} en consulta ${appointmentId}`);
    clearActionState();
    
    try {
      let success = false;
      
      switch (action) {
        case 'confirmar':
          success = await updateAppointmentStatus(user.id, appointmentId, 'confirmed');
          break;
        case 'completar':
          success = await updateAppointmentStatus(user.id, appointmentId, 'completed');
          break;
        case 'cancelar':
          success = await updateAppointmentStatus(user.id, appointmentId, 'cancelled', undefined, 'Cancelación por motivos médicos');
          break;
        default:
          console.warn('Acción no reconocida:', action);
          return;
      }
      
      if (success) {
        alert(`✅ Consulta ${action} exitosamente`);
      } else {
        alert(`❌ Error al ${action} la consulta`);
      }
    } catch (error: any) {
      console.error(`Error en acción ${action}:`, error);
      // Detectar error 403 - Suscripción vencida
      if (error?.message?.includes('suscripción ha vencido')) {
        setShowExpiredModal(true);
        return;
      }
      alert(`❌ Error al ${action} la consulta`);
    }
  };

  const renderAppointmentActions = (appointment: any) => {
    const availableActions = getAvailableActions(appointment);
    const isPastDate = isDateInPast(appointment.appointment_date);
    const isTodayDate = isDateToday(appointment.appointment_date);

    if (availableActions.length === 0) {
      return (
        <div className="flex flex-col space-y-2">
          <span className="text-xs text-gray-500 px-3 py-1.5 bg-gray-100/50 backdrop-blur-sm rounded-lg text-center">
            {isPastDate ? 'Consulta pasada' : 'Sin acciones'}
          </span>
        </div>
      );
    }

    return (
      <div className="flex flex-col space-y-2">
        {isTodayDate && (
          <span className="text-xs text-blue-600 font-medium px-3 py-1.5 bg-blue-100/50 backdrop-blur-sm rounded-lg text-center flex items-center justify-center gap-1">
            <Calendar className="w-3 h-3" />
            HOY
          </span>
        )}
        
        {availableActions.includes('confirmar') && (
          <button
            onClick={() => handleAppointmentAction(appointment.id, 'confirmar')}
            disabled={actionLoading}
            className="bg-green-100/50 backdrop-blur-sm text-green-700 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-green-200/50 disabled:opacity-50 transition-all flex items-center justify-center gap-1"
          >
            <CheckCircle className="w-3.5 h-3.5" />
            Confirmar
          </button>
        )}
        
        {availableActions.includes('completar') && (
          <button
            onClick={() => handleAppointmentAction(appointment.id, 'completar')}
            disabled={actionLoading}
            className="bg-blue-100/50 backdrop-blur-sm text-blue-700 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-blue-200/50 disabled:opacity-50 transition-all flex items-center justify-center gap-1"
          >
            <FileText className="w-3.5 h-3.5" />
            Completar
          </button>
        )}
        
        {availableActions.includes('cancelar') && (
          <button
            onClick={() => handleAppointmentAction(appointment.id, 'cancelar')}
            disabled={actionLoading}
            className="bg-red-100/50 backdrop-blur-sm text-red-700 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-red-200/50 disabled:opacity-50 transition-all flex items-center justify-center gap-1"
          >
            <XCircle className="w-3.5 h-3.5" />
            Cancelar
          </button>
        )}
      </div>
    );
  };

  const renderAppointmentCard = (appointment: any) => {
    const statusColors = {
      pending: 'bg-yellow-100/50 text-yellow-700',
      confirmed: 'bg-green-100/50 text-green-700',
      completed: 'bg-blue-100/50 text-blue-700',
      cancelled: 'bg-red-100/50 text-red-700'
    };

    const statusLabels = {
      pending: 'Pendiente',
      confirmed: 'Confirmada',
      completed: 'Completada',
      cancelled: 'Cancelada'
    };

    return (
      <div key={appointment.id} className="bg-white/40 backdrop-blur-md border border-white/20 rounded-xl p-4 shadow-lg hover:shadow-xl transition-all">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-green-500 rounded-full flex items-center justify-center text-white font-semibold">
                {appointment.client_display_name?.charAt(0) || 'P'}
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{appointment.client_display_name || 'Paciente'}</h3>
                <p className="text-sm text-gray-600">{appointment.service_name}</p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2 text-sm text-gray-600">
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {appointment.appointment_time}
              </span>
              <span className={`px-2 py-1 rounded-lg text-xs font-medium backdrop-blur-sm ${statusColors[appointment.status as keyof typeof statusColors]}`}>
                {statusLabels[appointment.status as keyof typeof statusLabels]}
              </span>
              {appointment.location_name && (
                <span className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-purple-50 text-purple-700">
                  <MapPin className="w-3 h-3" />
                  {appointment.location_name}
                </span>
              )}
            </div>

            {/* Nota del turno: editable desde el dashboard principal */}
            <AppointmentNoteEditor
              appointmentId={appointment.id}
              value={appointment.notes}
              onSaved={() => refreshData()}
            />

            {appointment.patient_extra_data && Object.keys(appointment.patient_extra_data).length > 0 && (
              <div className="text-xs text-gray-500 bg-amber-50/50 rounded-lg p-2 flex flex-wrap gap-x-3 gap-y-1">
                {Object.entries(appointment.patient_extra_data).filter(([key]) => !['additional_service_ids', 'all_service_names'].includes(key)).map(([key, value]: [string, any]) => (
                  value && (
                    <span key={key}>
                      <span className="font-bold">{getExtraLabel(key)}:</span> {formatExtraValue(key, value)}
                    </span>
                  )
                ))}
              </div>
            )}
            <button
              onClick={() => {
                const params = new URLSearchParams();
                params.set('patient', appointment.client_display_name);
                if (appointment.patient_extra_data) {
                  const extra = appointment.patient_extra_data;
                  if (extra.dni) params.set('dni', extra.dni);
                  if (Object.keys(extra).length > 0) {
                    sessionStorage.setItem('appointmentExtraData', JSON.stringify(extra));
                  }
                }
                if (appointment.client_email) params.set('email', appointment.client_email);
                if (appointment.client_phone) params.set('phone', appointment.client_phone);
                params.set('fromAppointment', 'true');
                navigate(`/professional/clinical-records/new?${params.toString()}`);
              }}
              className="inline-flex items-center gap-1 text-xs text-purple-600 font-bold hover:text-purple-800 hover:bg-purple-50 rounded px-2 py-1 mt-1 transition-colors"
            >
              <ClipboardList className="w-3.5 h-3.5" />
              Crear HC con estos datos
            </button>

            {/* La receta se escribe durante la consulta. Sin esto había que
                salir del calendario, entrar a Recetas y volver a tipear el
                nombre y el DNI de alguien que está en pantalla.
                Solo aparece si la cuenta puede emitir: una secretaria no firma
                recetas y quien trabaja en estética no receta. */}
            {mostrarBotonReceta && (
              <button
                onClick={() => {
                  const params = new URLSearchParams({ nuevaReceta: '1' });
                  params.set('nombre', appointment.client_display_name || '');
                  const extra = appointment.patient_extra_data || {};
                  if (extra.dni) params.set('dni', String(extra.dni));
                  if (extra.sexo) params.set('sexo', String(extra.sexo));
                  if (extra.fecha_nacimiento) params.set('nacimiento', String(extra.fecha_nacimiento));
                  // Sin matrícula no se puede emitir, pero el botón se muestra
                  // igual: escondido, el médico nunca se entera de que existe.
                  // Lo lleva a cargarla, que es el camino que queremos.
                  navigate(
                    puedeEmitirRecetas
                      ? `/professional/prescriptions?${params.toString()}`
                      : '/professional/habilitar-recetas'
                  );
                }}
                className="inline-flex items-center gap-1 text-xs text-teal-700 font-bold hover:text-teal-900 hover:bg-teal-50 rounded px-2 py-1 mt-1 ml-1 transition-colors"
              >
                <FileSignature className="w-3.5 h-3.5" />
                Crear receta
              </button>
            )}
          </div>

          {renderAppointmentActions(appointment)}
        </div>
      </div>
    );
  };

  // =====================================================
  // FUNCIONES DE NAVEGACIÓN
  // =====================================================

  const handleNewAppointment = () => navigate('/professional/new-appointment');
  const handleServicesConfig = () => navigate('/professional/services-config');
  const handleScheduleConfig = () => navigate('/professional/schedule-config');
  const handlePatientManagement = () => navigate('/professional/patients');
  const handleCalendar = () => navigate('/professional/calendar');
  const handleMedicalReports = () => navigate('/professional/reports');
  const handleLocationsConfig = () => navigate('/professional/locations-config');

  const handlePrevDay = () => {
    const currentDate = new Date(selectedDate);
    currentDate.setDate(currentDate.getDate() - 1);
    setSelectedDate(currentDate.toISOString().split('T')[0]);
  };

  const handleNextDay = () => {
    const currentDate = new Date(selectedDate);
    currentDate.setDate(currentDate.getDate() + 1);
    setSelectedDate(currentDate.toISOString().split('T')[0]);
  };

  const handleToday = () => {
    setSelectedDate(getToday());
  };

  // --- Exportar agenda a Excel (via Backend) ---
  const exportAgendaToExcel = async (fromDate: string, toDate: string) => {
    if (!user?.id) return;
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    const token = localStorage.getItem('token');
    const response = await fetch(
      `${API_URL}/api/appointments/professional/${user.id}/export/excel?from=${fromDate}&to=${toDate}`,
      { headers: { 'Authorization': `Bearer ${token}` } }
    );
    if (!response.ok) throw new Error('Error al exportar agenda');
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `agenda_${fromDate}_${toDate}.xlsx`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  // Sección Pacientes: fichas reales (migración 023), no la lista derivada de turnos
  if (currentPath === '/professional/patients') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <DashboardProfileHeader professional={professional} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <PatientRecords />
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-green-50 to-teal-50 flex items-center justify-center">
        <LoadingSpinner size={48} />
      </div>
    );
  }

  // Si la suscripción expiró, mostrar SOLO el modal de pago (nada más)
  if (showExpiredModal) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-green-50 to-teal-50">
        <SubscriptionExpiredModal
          show={true}
          subscriptionStatus={localStorage.getItem('subscription_status') || 'trial_expired'}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-green-50 to-teal-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        
        {/* Banner de suscripción */}
        <SubscriptionBanner onSubscribeClick={() => setShowSubscriptionModal(true)} />

        {/* Cuándo vence, a la vista. El dato existía solo en "Mi suscripción",
            y a esa pantalla se llega nada más que desde el menú del Header:
            hay que ir a buscar algo que no sabés que existe. */}
        <div className="mb-4">
          <VencimientoSuscripcion />
        </div>

        {/* Banner finito: "vence mañana / vence hoy" — auto-dismiss a los 5s */}
        <SubscriptionExpirySoonBanner
          subscription={user && (user as any).subscription}
          onPayClick={() => setShowSubscriptionModal(true)}
        />

        {/* Header del perfil */}
        <div className="animate-slide-in-left" style={{ animationDelay: '0ms' }}>
          <DashboardProfileHeader professional={professional} />
        </div>
 {/* 🆕 Link de reservas + Acceso rápido a recordatorios */}
        {user && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 animate-slide-in-right" style={{ animationDelay: '70ms' }}>
            {professional?.slug && (
              <ShareProfileButton
                professionalId={user.id}
                slug={professional.slug}
                className="mb-0"
                professionalData={{
                  nombre: professional.nombre,
                  apellido: professional.apellido,
                  especialidad: professional.especialidad,
                  professional_type: professional.professional_type,
                  business_name: professional.business_name,
                  picture: professional.picture,
                }}
              />
            )}
            <RemindersQuickAccess
              professionalId={user.id}
              onOpen={() => setShowRemindersPanel(true)}
              onOpenGuide={() => setShowRemindersGuide(true)}
            />
          </div>
        )}
        {/* Métricas principales */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 animate-slide-in-left" style={{ animationDelay: '140ms' }}>
          {loadingMetrics ? (
            <>
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white/40 backdrop-blur-md border border-white/20 rounded-xl p-4 animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded w-12"></div>
                </div>
              ))}
            </>
          ) : metricsError ? (
            <div className="col-span-2 md:col-span-4 bg-red-100/50 backdrop-blur-sm border border-red-200 rounded-xl p-4 text-red-600 text-center">
              Error cargando métricas
            </div>
          ) : (
            <>
              <div 
                onClick={() => document.getElementById('calendar-section')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-white/40 backdrop-blur-md border border-white/20 rounded-xl p-4 shadow-lg hover:shadow-xl transition-all cursor-pointer hover:scale-[1.02] active:scale-[0.98]"
              >
                <p className="text-sm text-gray-600 mb-1">Consultas Hoy</p>
                <p className="text-3xl font-bold text-blue-600">{metrics?.todayAppointments || 0}</p>
              </div>
              <div 
                onClick={() => document.getElementById('calendar-section')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-white/40 backdrop-blur-md border border-white/20 rounded-xl p-4 shadow-lg hover:shadow-xl transition-all cursor-pointer hover:scale-[1.02] active:scale-[0.98]"
              >
                <p className="text-sm text-gray-600 mb-1">Esta Semana</p>
                <p className="text-3xl font-bold text-green-600">{metrics?.weeklyAppointments || 0}</p>
              </div>
              <div 
                onClick={handlePatientManagement}
                className="bg-white/40 backdrop-blur-md border border-white/20 rounded-xl p-4 shadow-lg hover:shadow-xl transition-all cursor-pointer hover:scale-[1.02] active:scale-[0.98]"
              >
                <p className="text-sm text-gray-600 mb-1">Pacientes Activos</p>
                <p className="text-3xl font-bold text-teal-600">{metrics?.totalClients || 0}</p>
              </div>
              <div 
                onClick={() => document.getElementById('calendar-section')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-white/40 backdrop-blur-md border border-white/20 rounded-xl p-4 shadow-lg hover:shadow-xl transition-all cursor-pointer hover:scale-[1.02] active:scale-[0.98]"
              >
                <p className="text-sm text-gray-600 mb-1">Pendientes</p>
                <p className="text-3xl font-bold text-orange-600">{metrics?.pendingAppointments || 0}</p>
              </div>
            </>
          )}
        </div>

        {/* Sección compacta: WhatsApp + Calendario + Nueva Consulta */}
        <div id="calendar-section" className="bg-white/60 backdrop-blur-md border border-white/20 rounded-2xl shadow-lg overflow-hidden animate-slide-in-right" style={{ animationDelay: '210ms' }}>
          {/* WhatsApp inline */}
          <div className="px-3 pt-3 pb-1">
            <WhatsAppQuotaIndicator />
          </div>

          {/* Navegación de fecha compacta */}
          <div className="px-4 py-3 flex items-center justify-between border-t border-gray-100">
            <button
              onClick={handlePrevDay}
              className="p-2 rounded-xl bg-gray-100 hover:bg-gray-200 active:scale-95 transition-all"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>

            <div className="flex items-center gap-2">
              <button
                onClick={handleToday}
                className="bg-gradient-to-r from-blue-500 to-green-500 text-white px-4 py-1.5 rounded-full text-sm font-semibold hover:shadow-md transition-all active:scale-95"
              >
                Hoy
              </button>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="bg-gray-50 border border-gray-200 rounded-xl px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <button
              onClick={handleNextDay}
              className="p-2 rounded-xl bg-gray-100 hover:bg-gray-200 active:scale-95 transition-all"
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          {/* Nueva consulta integrado */}
          <button
            onClick={handleNewAppointment}
            className="w-full bg-gradient-to-r from-blue-500 to-green-500 text-white py-3 font-semibold text-base hover:shadow-lg transition-all flex items-center justify-center gap-2 active:scale-[0.98]"
          >
            <Plus className="w-5 h-5" />
            Nueva Consulta
          </button>
        </div>

        {/* Grid de acciones rápidas */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 animate-slide-in-left" style={{ animationDelay: '280ms' }}>
          <button 
            onClick={handleServicesConfig}
            className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm active:scale-95 transition-transform md:hover:shadow-md"
          >
            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-orange-500 md:bg-gradient-to-br md:from-orange-400 md:to-red-500 rounded-xl flex items-center justify-center mx-auto">
                <Settings className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-700 block">Servicios</span>
            </div>
          </button>
          
          <button
            onClick={handleScheduleConfig}
            className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm active:scale-95 transition-transform md:hover:shadow-md"
          >
            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-blue-500 md:bg-gradient-to-br md:from-cyan-400 md:to-blue-500 rounded-xl flex items-center justify-center mx-auto">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-700 block">Horarios</span>
            </div>
          </button>

          <button
            onClick={handleLocationsConfig}
            className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm active:scale-95 transition-transform md:hover:shadow-md"
          >
            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-purple-500 md:bg-gradient-to-br md:from-purple-400 md:to-violet-500 rounded-xl flex items-center justify-center mx-auto">
                <MapPin className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-700 block">Sedes</span>
            </div>
          </button>

          <button
            onClick={() => navigate('/professional/portfolio')}
            className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm active:scale-95 transition-transform md:hover:shadow-md"
          >
            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-indigo-500 md:bg-gradient-to-br md:from-blue-400 md:to-indigo-500 rounded-xl flex items-center justify-center mx-auto">
                <Image className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-700 block">Fotos de tus trabajos</span>
            </div>
          </button>
          
          <button 
            onClick={handlePatientManagement}
            className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm active:scale-95 transition-transform md:hover:shadow-md"
          >
            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-green-500 md:bg-gradient-to-br md:from-green-400 md:to-emerald-500 rounded-xl flex items-center justify-center mx-auto">
                <Users className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-700 block">Pacientes</span>
            </div>
          </button>
          
          <button 
            onClick={handleCalendar}
            className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm active:scale-95 transition-transform md:hover:shadow-md"
          >
            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-indigo-500 md:bg-gradient-to-br md:from-blue-400 md:to-indigo-500 rounded-xl flex items-center justify-center mx-auto">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-700 block">Calendario</span>
            </div>
          </button>
          
          <button 
            onClick={() => navigate('/professional/payment-config')}
            className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm active:scale-95 transition-transform md:hover:shadow-md"
          >
            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-teal-500 md:bg-gradient-to-br md:from-green-400 md:to-teal-500 rounded-xl flex items-center justify-center mx-auto">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-700 block">Pagos</span>
            </div>
          </button>
          
          <button 
            onClick={() => navigate('/professional/notification-config')}
            className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm active:scale-95 transition-transform md:hover:shadow-md"
          >
            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-green-500 md:bg-gradient-to-br md:from-green-400 md:to-emerald-500 rounded-xl flex items-center justify-center mx-auto">
                <MessageCircle className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-700 block">WhatsApp</span>
            </div>
          </button>
          
          <button 
            onClick={() => navigate('/professional/payment-verification')}
            className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm active:scale-95 transition-transform md:hover:shadow-md"
          >
            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-amber-500 md:bg-gradient-to-br md:from-orange-400 md:to-amber-500 rounded-xl flex items-center justify-center mx-auto">
                <CreditCard className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-700 block">Verificar Transferencias</span>
            </div>
          </button>
          
          <button 
            onClick={handleMedicalReports}
            className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm active:scale-95 transition-transform md:hover:shadow-md"
          >
            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-purple-500 md:bg-gradient-to-br md:from-indigo-400 md:to-purple-500 rounded-xl flex items-center justify-center mx-auto">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-700 block">Reportes</span>
            </div>
          </button>

                  {/* ✅ NUEVO BOTÓN: Historias Clínicas */}
          <button 
            onClick={() => navigate('/professional/clinical-records')}
            className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm active:scale-95 transition-transform md:hover:shadow-md"
          >
            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-teal-500 md:bg-gradient-to-br md:from-teal-400 md:to-cyan-500 rounded-xl flex items-center justify-center mx-auto">
                <ClipboardList className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-700 block">Historias Clínicas</span>
            </div>
          </button>

          {/* ✅ NUEVO BOTÓN: Datos adicionales del turno */}
          <button
            onClick={() => navigate('/professional/booking-fields-config')}
            className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm active:scale-95 transition-transform md:hover:shadow-md"
          >
            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-orange-500 md:bg-gradient-to-br md:from-orange-400 md:to-amber-500 rounded-xl flex items-center justify-center mx-auto">
                <ListChecks className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-700 block">Datos del Turno</span>
            </div>
          </button>

          {/* Recetas Electrónicas — el modal verifica habilitación contra REFEPS */}
          <button
            onClick={() => {
              // El cartelito "Nuevo" cumplió su función apenas entró una vez.
              // Dejarlo puesto para siempre lo convierte en decoración y deja
              // de avisar cuando de verdad haya algo nuevo.
              setBadgeRecetasVisto(true);
              localStorage.setItem('consultar:badge_recetas_visto', '1');
              setShowEligibilityModal(true);
            }}
            className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm active:scale-95 transition-transform md:hover:shadow-md relative"
          >
            {!badgeRecetasVisto && (
              <span className="absolute -top-2 -right-2 bg-[#0FA3B1] text-white text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full shadow-sm">
                Nuevo
              </span>
            )}
            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-emerald-500 md:bg-gradient-to-br md:from-emerald-500 md:to-teal-600 rounded-xl flex items-center justify-center mx-auto">
                <FileSignature className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-700 block">Recetas Electrónicas</span>
            </div>
          </button>
        </div>

        {/* Lista de consultas - Full Width */}
        <div className="bg-white/40 backdrop-blur-md border border-white/20 rounded-xl shadow-lg">
          <div className="p-6 border-b border-white/20">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Consultas para {formatLocalDate(selectedDate)}
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowRemindersPanel(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors text-sm font-medium shadow-sm"
                  title="Ver turnos para enviar recordatorios por WhatsApp"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span className="hidden sm:inline">Recordatorios</span>
                </button>
                <button
                  onClick={() => setShowRemindersGuide(true)}
                  className="flex items-center justify-center w-8 h-8 text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors"
                  title="¿Cómo funcionan los recordatorios?"
                  aria-label="Ver guía de recordatorios"
                >
                  <HelpCircle className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setShowExportModal(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 hover:bg-green-100 text-green-700 border border-green-200 rounded-lg transition-colors text-sm font-medium"
                  title="Exportar agenda a Excel"
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  <span className="hidden sm:inline">Excel</span>
                </button>
                {loadingAppointments && (
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                )}
              </div>
            </div>
          </div>
          <div className="p-6">
            {appointmentsError ? (
              <div className="text-center py-8">
                <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <p className="text-red-600 mb-4">Error cargando consultas: {appointmentsError}</p>
                <button 
                  onClick={() => user?.id && refreshData()}
                  className="bg-red-100/50 backdrop-blur-sm text-red-600 px-4 py-2 rounded-lg hover:bg-red-200/50 transition-all"
                >
                  Reintentar
                </button>
              </div>
            ) : loadingAppointments ? (
              <div className="space-y-4">
                {/* Mensaje explicito de carga para que el medico/secre no piense que se bugueo */}
                <div className="flex items-center justify-center gap-3 py-3 bg-blue-50/70 border border-blue-100 rounded-lg">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                  <p className="text-sm text-blue-700 font-medium">Cargando consultas del día...</p>
                </div>
                {[1, 2, 3].map(i => (
                  <div key={i} className="animate-pulse">
                    <div className="h-20 bg-gray-200/50 backdrop-blur-sm rounded-lg"></div>
                  </div>
                ))}
              </div>
            ) : appointments.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calendar className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No hay consultas programadas</h3>
                <p className="text-gray-600 mb-6">
                  No tienes consultas para {formatLocalDate(selectedDate)}
                </p>
                <button
                  onClick={handleNewAppointment}
                  className="bg-gradient-to-r from-blue-500 to-green-500 text-white px-6 py-3 rounded-lg hover:shadow-lg transition-all inline-flex items-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Programar Primera Consulta
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {appointments.map(renderAppointmentCard)}
              </div>
            )}
          </div>
        </div>

        {/* Error de acciones */}
        {actionError && (
          <div className="fixed bottom-4 right-4 bg-red-100/90 backdrop-blur-md border border-red-400 text-red-700 px-4 py-3 rounded-lg shadow-lg z-50">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              <span>{actionError}</span>
              <button 
                onClick={clearActionState}
                className="ml-4 text-red-500 hover:text-red-700"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Modal de suscripción */}
        <SubscriptionModal
          isOpen={showSubscriptionModal}
          onClose={() => setShowSubscriptionModal(false)}
          daysRemaining={daysRemaining}
          isExpired={isExpired}
        />

        {/* Modal de suscripción vencida */}
        <SubscriptionExpiredModal 
          show={showExpiredModal} 
          subscriptionStatus={localStorage.getItem('subscription_status') || 'trial_expired'} 
        />

        {/* 🆕 CHECKER DE ONBOARDING - VERIFICACIÓN INDEPENDIENTE */}
        {user && !showExpiredModal && (
          <OnboardingChecker
            userId={user.id}
            slug={professional?.slug}
            userName={user.nombre}
            userType="salud"
            especialidad={user.especialidad}
            onComplete={() => {
              console.log('✅ Onboarding completado - Recargando datos...');
              refreshData();
            }}
            onSkip={() => {
              console.log('⏭️ Onboarding saltado');
            }}
          />
        )}

        {/* Panel de debug solo en desarrollo */}
        {process.env.NODE_ENV === 'development' && (
          <div className="bg-blue-50/90 backdrop-blur-sm border border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-blue-800 mb-2">🔧 Estado de Conexión (Solo desarrollo)</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs text-blue-700">
              <div>
                <span className="font-medium">Métricas:</span>
                <span className={loadingMetrics ? 'text-yellow-600' : metricsError ? 'text-red-600' : 'text-green-600'}>
                  {loadingMetrics ? ' Cargando...' : metricsError ? ' Error' : ' ✓ Conectado'}
                </span>
              </div>
              <div>
                <span className="font-medium">Consultas:</span>
                <span className={loadingAppointments ? 'text-yellow-600' : appointmentsError ? 'text-red-600' : 'text-green-600'}>
                  {loadingAppointments ? ' Cargando...' : appointmentsError ? ' Error' : ` ✓ ${appointments.length} consultas`}
                </span>
              </div>
              <div>
                <span className="font-medium">Servicios:</span>
                <span className={loadingServices ? 'text-yellow-600' : servicesError ? 'text-red-600' : 'text-green-600'}>
                  {loadingServices ? ' Cargando...' : servicesError ? ' Error' : ` ✓ ${services.length} servicios`}
                </span>
              </div>
              <div>
                <span className="font-medium">Usuario:</span>
                <span className="text-green-600">✓ ID: {user?.id}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 🚀 OPTIMIZACIONES CRÍTICAS PARA MÓVIL */}
      <style>{`
        /* ============================================
           ELIMINAR EFECTOS PESADOS EN MÓVIL
           ============================================ */
        
        @media (max-width: 768px) {
          /* CRÍTICO: Deshabilitar backdrop-blur en móvil - Es SUPER pesado */
          [class*="backdrop-blur"] {
            backdrop-filter: none !important;
            -webkit-backdrop-filter: none !important;
          }
          
          /* Convertir fondos semi-transparentes a sólidos */
          .bg-white\\/40 {
            background-color: rgb(255, 255, 255) !important;
          }
          
          /* Simplificar todas las sombras */
          [class*="shadow-"] {
            box-shadow: 0 1px 2px rgba(0,0,0,0.05) !important;
          }
          
          /* ⚠️ NO eliminar TODOS los gradientes - Solo mantener colores sólidos en iconos de botones */
          /* Los gradientes de logo, banners, etc. se mantienen */
          
          /* Deshabilitar transiciones pesadas en móvil (excepto animaciones de
             entrada, que son transform+opacity y van aceleradas por GPU).
             El panel de Recordatorios (drawer y backdrop) también: sin
             excepción, acá quedaba en 0,01 ms y el despliegue lateral no se
             veía en el celular. Y la entrada del asistente de primeros pasos
             (slide-up) y su cambio de paso (panel-in, un solo bloque). */
          *:not(.animate-spin):not(.animate-pulse):not(.animate-slide-in-left):not(.animate-slide-in-right):not(.animate-modal-in):not(.animate-drawer-in):not(.animate-drawer-out):not(.animate-backdrop-in):not(.animate-backdrop-out):not(.animate-slide-up):not(.animate-panel-in) {
            transition: none !important;
            animation-duration: 0.01ms !important;
          }

          /* Mantener spinners y pulsos con velocidad normal */
          .animate-spin {
            animation: spin 1s linear infinite !important;
          }
          .animate-pulse {
            animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite !important;
          }
          /* Animaciones de entrada: mantener su velocidad normal en móvil */
          .animate-slide-in-left  { animation-duration: 0.5s !important; }
          .animate-slide-in-right { animation-duration: 0.5s !important; }
          .animate-modal-in       { animation-duration: 0.35s !important; }

          /* Excepto transform que es GPU-accelerated */
          .transition-transform {
            transition: transform 0.15s ease !important;
          }
        }
        
        /* Deshabilitar hover effects en móvil */
        @media (hover: none) and (pointer: coarse) {
          .hover\\:shadow-xl:hover,
          .hover\\:bg-white\\/70:hover,
          .hover\\:bg-green-200\\/50:hover,
          .hover\\:bg-blue-200\\/50:hover,
          .hover\\:bg-red-200\\/50:hover,
          .group-hover\\:scale-110 {
            box-shadow: inherit !important;
            background-color: inherit !important;
            transform: none !important;
          }
        }
        
        /* GPU acceleration - solo en elementos de contenido, NO en body/html */
        @media (max-width: 768px) {
          .gpu-accelerate {
            -webkit-transform: translateZ(0);
            transform: translateZ(0);
          }
          html, body {
            -webkit-overflow-scrolling: touch;
          }
        }
      `}</style>

      {/* Modal de Exportar Agenda */}
      <ExportDateRangeModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={exportAgendaToExcel}
        title="Exportar Agenda"
        description="Selecciona el rango de fechas para exportar"
      />

      {/* Panel de Recordatorios manuales (pivot migración 011) */}
      {user?.id && (
        <RemindersPanel
          professionalId={user.id}
          open={showRemindersPanel}
          onClose={() => setShowRemindersPanel(false)}
        />
      )}

      {/* Guía wizard de recordatorios manuales (primer uso) */}
      <NewRemindersGuideModal
        open={showRemindersGuide}
        onClose={() => setShowRemindersGuide(false)}
      />

      <PrescriptionEligibilityModal
        isOpen={showEligibilityModal}
        onClose={() => setShowEligibilityModal(false)}
        onContinuar={() => navigate('/professional/prescriptions')}
        onIrAPerfil={() => navigate('/professional/profile-config')}
        onIrASeguridad={() => navigate('/professional/profile-config?tab=security')}
      />


      {/* Modal Recetas Electrónicas - Trámite ReNaPDiS en evaluación */}
      {showPrescriptionPendingModal && ReactDOM.createPortal(
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setShowPrescriptionPendingModal(false); }}
          style={{
            position: 'fixed',
            inset: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overscrollBehavior: 'contain',
          }}
        >
          <div style={{
            background: 'white',
            width: 'calc(100% - 2rem)',
            maxWidth: '32rem',
            borderRadius: '1rem',
            padding: '1.75rem',
            margin: '1rem',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
          }}>
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileSignature className="w-8 h-8 text-amber-600" />
            </div>

            <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">
              Recetas Electrónicas
            </h3>

            <div className="inline-flex items-center justify-center gap-2 mx-auto mb-4 w-full">
              <span className="bg-amber-100 text-amber-800 text-xs font-bold px-3 py-1 rounded-full">
                EN TRÁMITE DE INSCRIPCIÓN
              </span>
            </div>

            <p className="text-gray-600 mb-3 text-sm leading-relaxed">
              ConsultAR ya tiene <strong>todos los servicios técnicos integrados</strong> con la
              Plataforma de Interoperabilidad del Ministerio de Salud de la Nación (REFEPS,
              Federador de Pacientes, PUCO, REFES y servicio de Recetas).
            </p>

            <p className="text-gray-600 mb-3 text-sm leading-relaxed">
              Estamos en <strong>proceso de evaluación oficial en el Registro Nacional de Plataformas Digitales Sanitarias (ReNaPDiS)</strong>.
              Una vez aprobada la solicitud, la Dirección Nacional de Sistemas de Información Sanitaria (DNSISa)
              nos asignará los códigos de Plataforma y Repositorio para emitir recetas válidas conforme a la
              <strong> Ley 27.553</strong> y la <strong>Resolución 2214/2025</strong>.
            </p>

            <p className="text-sm text-gray-500 mb-5 leading-relaxed">
              Tiempo estimado de aprobación: <strong>1 a 2 semanas hábiles</strong>.
              Te notificaremos por email cuando esté habilitada.
            </p>

            <button
              onClick={() => setShowPrescriptionPendingModal(false)}
              className="w-full px-6 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
            >
              Entendido
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default HealthProfessionalDashboard;